import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CreditCard,
  History,
  Mail,
  MapPin,
  MessageSquare,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermission } from '@/features/auth/auth.store';
import { VisitHistory } from '@/features/page-visits/visit-history';
import {
  formatDate,
  formatDateTime,
  formatPhone,
  formatRelative,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Customer, CustomerCard, CustomerLocation } from './types';

/** Card funding is a numeric enum on the API. */
const FUNDING_KEYS: Record<number, string> = {
  0: 'customers.funding.credit',
  1: 'customers.funding.debit',
  2: 'customers.funding.prepaid',
  3: 'customers.funding.unknown',
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * A consent flag, shown as a state rather than a control.
 *
 * The original rendered these as `<Switch disabled>` — a toggle you cannot
 * toggle reads as a broken control, not as read-only information.
 */
function Consent({
  icon: Icon,
  label,
  granted,
}: {
  icon: typeof Bell;
  label: string;
  granted: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm',
        granted
          ? 'border-success/25 bg-success/8 text-success'
          : 'text-muted-foreground border-dashed'
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={colSpan}
        className="text-muted-foreground py-8 text-center text-sm"
      >
        {message}
      </TableCell>
    </TableRow>
  );
}

function LocationsTable({ locations }: { locations: CustomerLocation[] }) {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>{t('customers.locationName')}</TableHead>
          <TableHead>{t('customers.address')}</TableHead>
          <TableHead>{t('customers.city')}</TableHead>
          <TableHead>{t('customers.zipcode')}</TableHead>
          <TableHead>{t('customers.status')}</TableHead>
          <TableHead>{t('customers.createdAt')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {locations.length === 0 && (
          <EmptyRow colSpan={6} message={t('customers.noLocations')} />
        )}
        {locations.map((location) => (
          <TableRow key={location.id}>
            <TableCell className="font-medium">
              {location.locationName || '—'}
            </TableCell>
            <TableCell className="max-w-[18rem] truncate">
              {location.locationAddress || '—'}
            </TableCell>
            <TableCell>{location.locationCity || '—'}</TableCell>
            <TableCell className="tabular-nums">
              {location.zipcode || '—'}
            </TableCell>
            <TableCell>
              <Badge variant={location.isActive ? 'success' : 'secondary'}>
                {location.isActive
                  ? t('customers.active')
                  : t('customers.inactive')}
              </Badge>
            </TableCell>
            <TableCell className="text-muted-foreground whitespace-nowrap">
              {formatDate(location.createdAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CardsTable({ cards }: { cards: CustomerCard[] }) {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>{t('customers.nickname')}</TableHead>
          <TableHead>{t('customers.reference')}</TableHead>
          <TableHead>{t('customers.type')}</TableHead>
          <TableHead>{t('customers.expires')}</TableHead>
          <TableHead>{t('customers.status')}</TableHead>
          <TableHead>{t('customers.createdAt')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {cards.length === 0 && (
          <EmptyRow colSpan={6} message={t('customers.noCards')} />
        )}
        {cards.map((card) => (
          <TableRow key={card.id} className={cn(card.wasDeleted && 'opacity-55')}>
            <TableCell className="font-medium">
              {card.nickname || '—'}
            </TableCell>
            <TableCell className="font-mono text-xs">
              {card.referenceCode || '—'}
            </TableCell>
            <TableCell>
              {card.funding !== null && FUNDING_KEYS[card.funding]
                ? t(FUNDING_KEYS[card.funding])
                : t('customers.funding.unknown')}
            </TableCell>
            <TableCell className="tabular-nums whitespace-nowrap">
              {card.expMonth && card.expYear
                ? `${String(card.expMonth).padStart(2, '0')}/${card.expYear}`
                : '—'}
            </TableCell>
            <TableCell>
              {/* Three independent booleans on the API; the row shows the one
                  that actually decides whether the card can be charged. */}
              {card.wasDeleted ? (
                <Badge variant="secondary" className="gap-1">
                  <Trash2 className="size-3" />
                  {t('customers.deleted')}
                </Badge>
              ) : card.passedSecurity ? (
                <Badge variant="success" className="gap-1">
                  <ShieldCheck className="size-3" />
                  {t('customers.verified')}
                </Badge>
              ) : (
                <Badge variant="warning">{t('customers.unverified')}</Badge>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground whitespace-nowrap">
              {formatDate(card.createdAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Everything known about one customer, opened from a row in the results.
 *
 * The original packed this into an expanded table row containing three nested
 * antd tables — on a phone that was a table inside a table inside a horizontal
 * scroller. Here the tabs stay, and each table scrolls in its own container.
 */
export function CustomerDetail({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const locations = customer.locations ?? [];
  const cards = customer.cards ?? [];

  // The API makes the same check on every request. This one only decides
  // whether to offer the tab, so a role that cannot read a history is not sent
  // to an empty panel to find that out.
  const canSeeHistory = usePermission('isCustomerService');
  // Held here rather than in the panel so they survive switching tabs, which is
  // what somebody comparing the history against an order actually does.
  const [historyWindow, setHistoryWindow] = useState<number | null>(30);
  // Null is every app this account has used. Support does not know which one a
  // caller is on until they say so, so the unfiltered list is the useful start.
  const [historyDomain, setHistoryDomain] = useState<string | null>(null);

  return (
    <Tabs defaultValue="details" className="w-full">
      <TabsList>
        <TabsTrigger value="details">{t('customers.details')}</TabsTrigger>
        <TabsTrigger value="locations">
          {t('customers.locations')}
          <span className="opacity-60 tabular-nums">({locations.length})</span>
        </TabsTrigger>
        <TabsTrigger value="cards">
          <CreditCard />
          {t('customers.cards')}
          <span className="opacity-60 tabular-nums">({cards.length})</span>
        </TabsTrigger>
        {canSeeHistory && (
          <TabsTrigger value="activity">
            <History />
            {t('customers.activity')}
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="details" className="space-y-5">
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t('customers.email')}>
            {customer.email ? (
              <span className="group flex items-center gap-1">
                <span className="truncate">{customer.email}</span>
                <CopyButton value={customer.email} />
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label={t('customers.phone')}>
            {formatPhone(customer.phoneNumber)}
          </Field>
          <Field label={t('customers.friendCode')}>
            {customer.friendCode ? (
              <span className="group flex items-center gap-1">
                <span className="font-mono text-xs">{customer.friendCode}</span>
                <CopyButton value={customer.friendCode} />
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label={t('customers.userSince')}>
            <span className="whitespace-nowrap">
              {formatDateTime(customer.createdAt)}
            </span>
            <span className="text-muted-foreground ml-1 text-xs">
              ({formatRelative(customer.createdAt)})
            </span>
          </Field>
          <Field label={t('customers.orderCount')}>
            <span className="tabular-nums">{customer.orderCount ?? 0}</span>
          </Field>
          <Field label={t('customers.accountStatus')}>
            {customer.deletedAt ? (
              <Badge variant="destructive">{t('customers.deleted')}</Badge>
            ) : (
              <Badge variant="success">{t('customers.active')}</Badge>
            )}
          </Field>
        </dl>

        <div className="space-y-2">
          <p className="text-muted-foreground text-xs font-medium">
            {t('customers.communicationPreferences')}
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <Consent
              icon={Bell}
              label={t('customers.allowPush')}
              granted={customer.allowPush}
            />
            <Consent
              icon={Mail}
              label={t('customers.allowEmails')}
              granted={customer.allowEmails}
            />
            <Consent
              icon={MessageSquare}
              label={t('customers.allowSms')}
              granted={customer.allowSMS}
            />
          </div>
        </div>
      </TabsContent>

      <TabsContent value="locations">
        <div className="rounded-lg border">
          <LocationsTable locations={locations} />
        </div>
      </TabsContent>

      <TabsContent value="cards">
        <div className="rounded-lg border">
          <CardsTable cards={cards} />
        </div>
      </TabsContent>

      {canSeeHistory && (
        <TabsContent value="activity">
          <VisitHistory
            userId={customer.id}
            windowDays={historyWindow}
            onWindowChange={setHistoryWindow}
            domain={historyDomain}
            onDomainChange={setHistoryDomain}
          />
        </TabsContent>
      )}
    </Tabs>
  );
}

/** Compact location summary for the phone layout, where a table cannot fit. */
export function LocationSummary({ customer }: { customer: Customer }) {
  const primary = customer.locations?.find((location) => location.isActive);
  if (!primary) return null;

  return (
    <span className="text-muted-foreground flex items-center gap-1 text-xs">
      <MapPin className="size-3 shrink-0" />
      <span className="truncate">
        {[primary.locationCity, primary.zipcode].filter(Boolean).join(', ')}
      </span>
    </span>
  );
}
